import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Badge, Box, Button, Drawer, IconButton, List, styled, } from '@mui/material';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import FactoryIcon from '@mui/icons-material/Factory';
import LogoutIcon from '@mui/icons-material/Logout';
import PrintIcon from '@mui/icons-material/Print';
import StarIcon from '@mui/icons-material/Star';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import logo from '../../../../public/trove_logo.png';
import { Alert } from '../../atoms/Alert';
import NavigationButton from './NavigationButton';
import NavigationDetail from './NavigationDetail';
const NavigationDrawer = styled(Drawer) `
  && > div.MuiPaper-root {
    background-color: #F5F5F5;

    width: 100%;
    ${({ theme }) => theme.breakpoints.up('desktop')} {
      width: 256px;
    }

    display: flex;
    flex-direction: column;
    transition: width .2s;
    overflow: hidden;
    border-right: 2px solid rgba(0, 0, 0, 0.12);
  }
`;
const TopSection = styled('div') `
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;
const NavigationOptionsContainer = styled(List) `
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: rgba(0,0,0,.5);
    -webkit-box-shadow: 0 0 1px rgba(255,255,255,.5);
  }
`;
const LargeLogo = styled('img') `
  max-height: 22px;
  cursor: pointer;
`;
const MenuLogoContainer = styled('div') `
  display: flex;
  align-items: center;
  flex-direction: 'row';
  margin-top: 12px;
  margin-bottom: 8px;
`;
const MenuButton = styled(IconButton) `
  margin: 12px;
  color: black;
`;
const LogoContainer = styled('div') `
  margin-left: 16px;
`;
/**
 * Calculate the most severe alert level from a list of alerts
 * @param alerts - Array of navigation alerts
 * @returns The most severe alert level ('warning' or 'info') or undefined if no alerts
 */
const getMostSevereAlertLevel = (alerts) => {
    if (!alerts || alerts.length === 0)
        return undefined;
    // Warning is more severe than info
    const hasWarning = alerts.some(alert => alert.severity === 'warning');
    if (hasWarning)
        return 'warning';
    // Default severity is 'info' if alerts exist but no severity is specified
    return 'info';
};
export const SideNavigation = ({ activeIndex = -1, currentSubModule = '', onClose = () => { }, onOpen = () => { }, navigationOptions, onClickLogout = () => { }, onClickLogo = () => { }, onClickAccount = () => { }, onClickPartner, onEditFacility, isExpanded = false, facility, partnerName, workstation, alerts, onAlertAction, }) => {
    // Calculate the most severe alert level from all alerts
    const alertLevel = React.useMemo(() => getMostSevereAlertLevel(alerts), [alerts]);
    // Deduplicate alerts and log errors if duplicates found
    const deduplicatedAlerts = React.useMemo(() => {
        if (!alerts || alerts.length === 0)
            return alerts;
        const seen = new Set();
        const duplicateIds = new Set();
        // First pass: identify duplicates
        alerts.forEach((alert) => {
            if (seen.has(alert.id)) {
                duplicateIds.add(alert.id);
            }
            else {
                seen.add(alert.id);
            }
        });
        // Log errors for duplicates
        if (duplicateIds.size > 0) {
            console.error(`[SideNavigation] Duplicate alert IDs detected: ${Array.from(duplicateIds).join(', ')}. ` +
                'Each alert must have a unique ID. Only the last occurrence of each duplicate will be displayed.');
        }
        // Use Map to keep only the last occurrence of each ID
        const alertMap = new Map(alerts.map(alert => [alert.id, alert]));
        return Array.from(alertMap.values());
    }, [alerts]);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const toggleDrawer = (event) => {
        if (isExpanded) {
            onClose(event);
        }
        else {
            onOpen(event);
        }
    };
    const renderNavigationButtons = () => {
        const navigationButtons = navigationOptions.map(({ icon, label, onClick, subModules, }, index) => (_jsx(NavigationButton, { onClick: onClick, icon: icon, label: label, isActive: index === activeIndex, currentSubModule: currentSubModule, subModules: subModules }, label)));
        return (_jsx(NavigationOptionsContainer, { children: navigationButtons }));
    };
    const handleEditFacility = () => {
        onEditFacility?.();
    };
    const handleSelectPartner = () => {
        if (onClickPartner)
            onClickPartner();
    };
    return (_jsxs(NavigationDrawer, { variant: "temporary", open: isExpanded, onClose: () => { onClose(); }, children: [_jsxs(TopSection, { children: [_jsxs(MenuLogoContainer, { isDrawerOpen: isExpanded, children: [_jsx(MenuButton, { onClick: (event) => { toggleDrawer(event); }, children: _jsx(Badge, { variant: "dot", color: alertLevel, invisible: !alertLevel, children: _jsx(ViewHeadlineIcon, {}) }) }), _jsx(LogoContainer, { children: _jsx(LargeLogo, { src: logo, alt: "Trove logo", onClick: onClickLogo }) })] }), renderNavigationButtons()] }), _jsxs("div", { children: [deduplicatedAlerts && deduplicatedAlerts.length > 0 && (_jsx(Box, { px: 1, display: "flex", flexDirection: "column", gap: 0.5, children: deduplicatedAlerts.map(alert => (_jsx(Alert, { severity: alert.severity ?? 'info', title: alert.title, shouldHideIcon: true, variant: 'outlined', sx: {
                                '& .MuiAlert-message': {
                                    width: '100%',
                                    padding: 0,
                                }
                            }, children: _jsxs(Box, { display: "flex", flexDirection: "column", gap: 0.5, sx: { width: '100%' }, children: [alert.message, _jsx(Button, { size: "small", onClick: () => onAlertAction?.(alert), fullWidth: true, color: alert.severity, variant: "contained", children: alert.actionLabel })] }) }, alert.id))) })), _jsxs(List, { children: [_jsx(NavigationButton, { onClick: onClickAccount, icon: _jsx(AccountCircleRoundedIcon, {}), label: "Account" }), _jsxs(Box, { paddingTop: '8px', paddingBottom: '8px', children: [_jsx(NavigationDetail, { isExpanded: isExpanded, icon: _jsx(FactoryIcon, {}), label: facility ?? 'Facility', onEdit: handleEditFacility }), _jsx(NavigationDetail, { isExpanded: isExpanded, icon: _jsx(PrintIcon, {}), label: workstation ?? 'Workstation' })] }), partnerName &&
                                _jsx(NavigationDetail, { isExpanded: isExpanded, icon: _jsx(StarIcon, {}), label: partnerName, onEdit: handleSelectPartner }), _jsx(NavigationButton, { onClick: onClickLogout, icon: _jsx(LogoutIcon, {}), label: "Log out" })] })] })] }));
};
//# sourceMappingURL=index.js.map